import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ReactComponent as CaseloadIcon } from "../../../../../assets/images/coach-metrics/caseload.svg";
import { ReactComponent as CoachActivitiesIcon } from "../../../../../assets/images/coach-metrics/coach-activities.svg";
import { ReactComponent as CoachMetricsIcon } from "../../../../../assets/images/coach-metrics/coach-metrics-icon.svg";
import { ReactComponent as NavigationLeftIcon } from "../../../../../assets/images/coach-metrics/icon-navigation-left.svg";
import { ReactComponent as NavigationRightIcon } from "../../../../../assets/images/coach-metrics/icon-navigation-right.svg";
import { ReactComponent as ParticipantsLandedIcon } from "../../../../../assets/images/coach-metrics/participants-landed.svg";
import {
  MuiBox,
  MuiIconButton,
  MuiSelect,
  OptionsDataType,
} from "../../../../../components";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import { COACH_METRICS_DATE_RANGE } from "../../../../../helpers/constants";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import {
  COACH_METRICS_DASHBOARD_PERIOD_VALUES,
  CoachMetricsCardTypes,
} from "../../../modelsAndSchemas/Models";
import { coachMetricsSiteCoreKeys } from "../../../sitecore/SiteCoreKeys";
import {
  CPDashboardCoachMetricsCard,
  CPDashboardCoachMetricsCardProps,
} from "./components/CPDashboardCoachMetricsCard";
import { CPDashboardCoachMetricsWrapper } from "./CPDashboardCoachMetrics.style";

export interface CoachMetricsDetails {
  assignedProgramsCount: string;
  programsWithoutEngagementCount: string;
  completedActivitiesCount: string;
  totalHoursSpent: string;
  participantsLandedCount: string;
}
interface CPDashboardCoachMetricsProps {
  onCardClick: (type: CoachMetricsCardTypes) => void;
  coachMetricsDetails: CoachMetricsDetails;
  onDateRangeChange: (range: string) => void;
}

export const CPDashboardCoachMetrics = ({
  onCardClick,
  coachMetricsDetails,
  onDateRangeChange,
}: CPDashboardCoachMetricsProps) => {
  const { fetchLabel, fetchComponentTitle, fetchDropdownValues, commonLabels } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [rangeType, setRangeType] = useState<OptionsDataType[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { isMobile } = useMediaQueries();

  // Get last 2 years as options for dropdown
  const lastTwoYearsOptions: OptionsDataType[] = Array.from(
    { length: 2 },
    (_, i) => {
      const year = dayjs()
        .subtract(i + 1, "year")
        .year();
      let value = "";
      if (i == 0) {
        value = COACH_METRICS_DATE_RANGE.CURRENT_MINUS_ONE_YEAR;
      } else {
        value = COACH_METRICS_DATE_RANGE.CURRENT_MINUS_TWO_YEAR;
      }
      return {
        label: year.toString(),
        value: value,
      };
    },
  );

  useEffect(() => {
    if (!siteCoreLoading) {
      setRangeType([
        ...fetchDropdownValues(
          coachMetricsSiteCoreKeys.coachMetricsTitle,
          coachMetricsSiteCoreKeys.periodList,
        ),
        ...lastTwoYearsOptions,
      ]);
    }
  }, [siteCoreLoading]);

  const getLabel = (childKey: string) => {
    return fetchLabel(coachMetricsSiteCoreKeys.coachMetricsTitle, childKey);
  };

  const handleLeftScrollAction = (): void => {
    scroll("left");
  };
  const handleRightScrollAction = (): void => {
    scroll("right");
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 300; // pixels per click
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const dashboardMetricsArray: CPDashboardCoachMetricsCardProps[] = [
    {
      header: getLabel(
        coachMetricsSiteCoreKeys.caseloadsVsEngagementCardHeader,
      ),
      topIcon: <CaseloadIcon />,
      onCardClick: () => onCardClick("caseload_vs_engagement"),
      type: "caseload_vs_engagement",
      showArrow: true,
      label: getLabel(
        coachMetricsSiteCoreKeys.caseloadsVsEngagementCardAssigned,
      ),
      labelSecondary: getLabel(
        coachMetricsSiteCoreKeys.caseloadsVsEngagementCardNoEngagement,
      ),
      value: coachMetricsDetails?.assignedProgramsCount,
      valueSecondary: coachMetricsDetails?.programsWithoutEngagementCount,
    },
    {
      header: getLabel(coachMetricsSiteCoreKeys.coachingActivitiesCardHeader),
      topIcon: <CoachActivitiesIcon />,
      onCardClick: () => onCardClick("coach_activities"),
      type: "coach_activities",
      label: getLabel(coachMetricsSiteCoreKeys.coachingActivitiesCardCompleted),
      labelSecondary: getLabel(
        coachMetricsSiteCoreKeys.coachingActivitiesCardTimeSpent,
      ),
      value: coachMetricsDetails?.completedActivitiesCount,
      valueSecondary: coachMetricsDetails?.totalHoursSpent,
    },
    {
      header: getLabel(coachMetricsSiteCoreKeys.participantsLandedCardHeader),
      topIcon: <ParticipantsLandedIcon />,
      onCardClick: () => onCardClick("participants_landed"),
      type: "participants_landed",
      showArrow: true,
      label: getLabel(
        coachMetricsSiteCoreKeys.participantsLandedCardSecuredAJob,
      ),
      value: coachMetricsDetails?.participantsLandedCount,
    },
  ];

  return (
    <CPDashboardCoachMetricsWrapper>
      <MuiBox>
        <MuiBox className="coach-metrics-info-top">
          <MuiEllipseLabel
            labelTypoVariant="h2SemiBold"
            text={fetchComponentTitle(
              coachMetricsSiteCoreKeys.coachMetricsTitle,
            )}
            titleIcon={<CoachMetricsIcon className="title-icon" />}
          />
          <MuiBox className="coach-metrics-info-top">
            <MuiSelect
              className="range-type-select"
              label={""}
              options={rangeType}
              defaultValue={COACH_METRICS_DASHBOARD_PERIOD_VALUES.LAST_6_MONTHS}
              visibleDefaultSelectOption={false}
              id={"range-type-select"}
              placeholder={commonLabels.selectPlaceHolder}
              isMandatory={false}
              requiredARIALabel={commonARIALabel.required}
              onChange={(e) => onDateRangeChange(e.target.value as string)}
            />

            <MuiIconButton
              onClick={handleLeftScrollAction}
              aria-label={commonARIALabel.scrollLeft}
              className={isMobile ? "d-none" : ""}
            >
              <NavigationLeftIcon aria-hidden="true" />
            </MuiIconButton>
            <MuiIconButton
              onClick={handleRightScrollAction}
              aria-label={commonARIALabel.scrollRight}
              className={isMobile ? "d-none" : ""}
            >
              <NavigationRightIcon aria-hidden="true" />
            </MuiIconButton>
          </MuiBox>
        </MuiBox>
        <MuiBox
          ref={scrollRef}
          className="coach-metrics-info"
          tabIndex={0}
          role="region"
          aria-label={commonARIALabel.metricsCarousel}
        >
          {dashboardMetricsArray.map((cardProps) => (
            <CPDashboardCoachMetricsCard key={cardProps.type} {...cardProps} />
          ))}
        </MuiBox>
      </MuiBox>
    </CPDashboardCoachMetricsWrapper>
  );
};
